import React, { FC, useState } from 'react';
import styled from 'styled-components';
import { SearchOutlined } from '@ant-design/icons';
import colors from '../../utils/colors';
import { DELETED, EMAIL, FIRST_NAME, ID, SEARCH_BY_EMAIL, USERS } from '../../utils/constants/labels';
import PageHeader from '../common/layout/PageHeader';
import Table from '../common/table/Table';
import { api } from '../../api/api';
import { Input, Tag } from 'antd';
import { debounce } from '../../utils/common';
import { RegularText } from '../../utils/typography';
import { cancellationReasonColors, hasValue, membershipTypeColors } from '../../utils/helpers';
import { DATE_FORMAT, formatDate } from '../../utils/timeUtils';

const columns = [
  {
    title: ID,
    dataIndex: 'id',
    sorter: true,
    width: '5%'
  },
  {
    title: FIRST_NAME,
    dataIndex: 'firstName',
    width: '10%'
  },
  {
    title: EMAIL,
    dataIndex: 'email',
    sorter: true,
    width: '15%'
  },
  {
    title: 'Membership',
    dataIndex: 'membershipType',
    width: '10%',
    render: value => <Tag color={membershipTypeColors[value]}>{value}</Tag>
  },
  {
    title: 'Cancellation Reasons',
    dataIndex: 'cancellationReasons',
    width: '45%',
    render: (value, row) => renderCancellationReasons(value)
  },
  {
    title: DELETED,
    dataIndex: 'deletedAt',
    sorter: true,
    width: '15%',
    defaultSortOrder: 'descend',
    render: (value, row) => (value ? row.deletedAt ? <span>{formatDate(row.deletedAt, DATE_FORMAT)}</span> : '' : '-')
  }
];

const DELETE_ACCOUNT_LABEL = 'Why do you want to permanently delete your Shovel account?';
const CANCEL_ACCOUNT_LABEL = 'Why do you want to permanently cancel your pricing plan?';

const renderCancellationReasons = (stringValue: string) => {
  if (!stringValue) return null;

  let value;

  try {
    value = JSON.parse(stringValue);
  } catch (e) {
    return (
      <CancellationReasons>
        <Reasons>
          <Experience>Deleted by admin</Experience>
        </Reasons>
      </CancellationReasons>
    );
  }

  if (!hasValue(value[DELETE_ACCOUNT_LABEL]) && !hasValue(value[CANCEL_ACCOUNT_LABEL])) return null;

  const isDelete = hasValue(value[DELETE_ACCOUNT_LABEL]);
  const reasons = isDelete ? value[DELETE_ACCOUNT_LABEL].split(';') : value[CANCEL_ACCOUNT_LABEL].split(';');

  return (
    <CancellationReasons>
      <Reasons>
        {reasons.map(r => (
          <Tag color={cancellationReasonColors[r]} key={r}>
            {r}
          </Tag>
        ))}
      </Reasons>
      <Experience>{value['experience']}</Experience>
    </CancellationReasons>
  );
};

type Props = {};

const Cancellations: FC<Props> = ({}) => {
  const [email, setEmail] = useState('');

  const setEmailDebounced = debounce(value => setEmail(value), 400);

  return (
    <Container>
      <PageHeader
        title={USERS}
        extra={[
          <Input
            key={1}
            onChange={e => setEmailDebounced(e.target.value)}
            placeholder={SEARCH_BY_EMAIL}
            prefix={<SearchOutlined />}
          />
        ]}
      />
      <Table
        columns={columns}
        getData={api.getCancellations}
        idPropName={'id'}
        searchPropName={'email'}
        searchPropValue={email}
        defaultSortingProp={'deletedAt'}
      />
    </Container>
  );
};

export default Cancellations;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  flex: 1;
  background-color: ${colors.white};
  position: relative;
`;

const CancellationReasons = styled.div`
  display: flex;
  flex-direction: column;
`;

const Reasons = styled.div`
  display: flex;
  flex-direction: row;
`;

// const Reason = styled(RegularText)`
//   display: inline-block;
//   margin-right: 5px;
//   padding: 3px 6px;
//   background-color: ${colors.primaryLight};
//   border-radius: 5px;
// `;

const Experience = styled(RegularText)`
  display: inline-block;
  margin-top: 5px;
`;
